import { IPoint2D } from 'flux-definition/src';
import { AbstractConnector } from './connector-abstract';

/**
 * This class contains the capability to draw a wavy
 * connector using the coordinate data stored into the model,
 * using the provided graphics object.
 *
 * To draw a wavy connector, the model should have
 * a path with each point the connector should connect to.
 *
 * @author  Nkweti Awa
 * @since   2022-05-11
 */
export class ConnectorDouble extends AbstractConnector {

    /**
     * Draws the wavy connector.
     */
    public draw() {
        const points = this.points || this.model.getPoints();
        if ( !points.length ) {
            return;
        }

        const firstPoint =  points[ 0 ];
        const lastPoint = points[ points.length - 1 ];

        const pos = this.getParallelPoints( firstPoint.x, firstPoint.y, lastPoint.x, lastPoint.y, 5 );
        const p1 = pos[ 0 ];
        const p2 = pos[ 1 ];
        const p3 = pos[ 2 ];
        const p4 = pos[ 3 ];

        this.moveTo( p1 );
        this.lineTo( p2 );

        this.moveTo( p3 );
        this.lineTo( p4);
        this.drawExtras( this.model.name, firstPoint, lastPoint );
    }

    /**
     * draw any extra designs we have on the connector
     * @param name the connector's name
     * @param firstPoint
     * @param lastPoint
     */
    public drawExtras( name: string, firstPoint: IPoint2D, lastPoint: IPoint2D ) {
        // this.graphics.setStrokeDash([]);
        // this.graphics.beginStroke( '#ffffff' );
        switch (name) {
            case 'Best Friends / Very Close':
                const dx = lastPoint.x - firstPoint.x;
                const dy = lastPoint.y - firstPoint.y;
                const lineLength = 24; // Length of each short perpendicular line
                const spacing = 20; // Desired spacing between short lines along the main line

                // Calculate the length of the main line
                const mainLineLength = Math.sqrt(dx * dx + dy * dy);
                if (mainLineLength < 0.0001) {
                    break; // Skip if points are too close
                }

                // Calculate number of lines based on line length and spacing
                const numLines = Math.floor(mainLineLength / spacing) + 1;
                const actualSpacing = mainLineLength / (numLines - 1 || 1); // Adjust spacing if numLines > 1

                // Unit vector along the main line
                const unitDx = dx / mainLineLength;
                const unitDy = dy / mainLineLength;

                // Perpendicular unit vector (-dy, dx)
                const perpDx = -unitDy;
                const perpDy = unitDx;

                // Draw short lines at equal intervals along the main line
                for (let i = 0; i < numLines; i++) {
                    // Parameter t ranges from 0 to 1 along the line
                    const t = i * actualSpacing / mainLineLength;
                    // Center point of the short line on the main line
                    const centerX = firstPoint.x + t * dx;
                    const centerY = firstPoint.y + t * dy;
                    // Endpoints of the short perpendicular line
                    const startX = centerX - (lineLength / 2) * perpDx;
                    const startY = centerY - (lineLength / 2) * perpDy;
                    const endX = centerX + (lineLength / 2) * perpDx;
                    const endY = centerY + (lineLength / 2) * perpDy;
                    this.moveTo({ x: startX, y: startY });
                    this.lineTo({ x: endX, y: endY });
                }
                break;
            case 'Committed Relationship':
                this.graphics.setStrokeDash([]);
                const pos = this.getParallelPoints( firstPoint.x, firstPoint.y, lastPoint.x, lastPoint.y, 5 );
                const p1 = pos[ 0 ];
                const p2 = pos[ 1 ];
                this.moveTo( p1 );
                this.lineTo( p2 );
                break;
            default:
                break;
        }
    }

}

Object.defineProperty( ConnectorDouble, 'name', {
    value: 'ConnectorDouble',
});
